import { Component, OnInit } from "@angular/core";
import { FormBuilder, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { IRegisterDetails, Sex } from "@gloria-public/register/domain/interfaces/registration.interface";
import { RegistrationService } from "@gloria-public/register/domain/services/registration.service";
import { CaptchaDialogComponent } from "@gloria-public/register/views/registration-page/components/captcha-dialog/captcha-dialog.component";
import { EmailConfirmationComponent } from "@gloria-public/register/views/registration-page/components/email-confirmation/email-confirmation.component";
import { TranslateService } from "@ngx-translate/core";
import { ConsentDialogComponent } from "@shared/components/consent-dialog/consent-dialog.component";
import { CountryService } from "@shared/services/country.service";
import { SessionService } from "@shared/services/session.service";
import Validation from "@shared/utils/helpers/validators/form-field-custom.validators";
import { Subject, distinctUntilChanged, finalize, first, startWith, takeUntil } from "rxjs";

export interface INgxIntlTelInput {
  countryCode: string;
  dialCode: string;
  e164Number: string;
  internationalNumber: string;
  nationalNumber: string;
  number: string;
}

@Component({
  selector: "app-registration-page",
  templateUrl: "./registration-page.component.html",
  styleUrls: ["./registration-page.component.scss"],
})
export class RegistrationPageComponent implements OnInit {
  public countries: { code: string; name: string }[] = [];
  public maxDate = new Date().toISOString().split("T")[0];
  public userForm = this.fb.nonNullable.group(
    {
      email: [
        "",
        [
          Validators.required,
          Validators.maxLength(255),
          Validators.pattern(/^[a-zA-Z0-9_+&*-]+(?:\.[a-zA-Z0-9_+&*-]+)*@(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/),
        ],
      ],
      password: ["", [Validators.required, Validators.maxLength(255), Validators.pattern(/^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}$/)]],
      confirmPassword: ["", [Validators.required, Validators.maxLength(255)]],
      firstname: ["", [Validators.required, Validators.maxLength(255)]],
      birthdate: ["", [Validators.required, Validation.futureDateValidator(this.maxDate)]],
      sex: [Sex.FEMALE, [Validators.required]],
      name: ["", [Validators.required, Validators.maxLength(255)]],
      phone: ["", [Validators.required]],
      street: ["", [Validators.required, Validators.maxLength(255)]],
      number: ["", [Validators.required, Validators.maxLength(10)]],
      city: ["", [Validators.required, Validators.maxLength(255)]],
      postalCode: ["", [Validators.required, Validators.maxLength(10)]],
      country: ["", [Validators.required, Validators.maxLength(50)]],
      nationalNumber: ["", [Validators.required, Validators.maxLength(20)]],
    },
    {
      validators: [Validation.match("password", "confirmPassword")],
    }
  );
  public showPassword = false;
  public showConfirmPassword = false;

  /** Emits when the component is destroyed, used to complete all active subscriptions */
  private onDestroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private registrationService: RegistrationService,
    private translateService: TranslateService,
    private sessionService: SessionService,
    private countryService: CountryService,
    public snackBar: MatSnackBar,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.translateService.onLangChange
      .pipe(
        startWith({
          lang: this.translateService.currentLang,
        }),
        distinctUntilChanged((a, b) => a.lang === b.lang),
        takeUntil(this.onDestroy$)
      )
      .subscribe((event) => {
        this.countries = this.countryService.getCountries(event.lang);

        if (this.countries.length > 0 && !this.userForm.get("country")?.value) {
          this.userForm.patchValue({
            country: this.countries[0].code,
          });
        }
      });
  }

  /**
   * Formats a date string into dd/mm/yyyy format
   * @param date input date string
   * @return formatted date string
   */
  private formatDate(date: string): string {
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  }

  /**
   * Handles user registration submission including consent approval, captcha validation and API call
   */
  public onSave(): void {
    if (this.userForm.invalid) {
      this.userForm.markAllAsTouched();
      return;
    }

    this.userForm.disable();

    const consentDialogRef = this.dialog.open(ConsentDialogComponent, {
      disableClose: true,
      data: { skipAcceptCall: true },
      width: "90vw",
      maxWidth: "1200px",
      maxHeight: "90vh",
      autoFocus: false,
      restoreFocus: false,
    });

    consentDialogRef
      .afterClosed()
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => this.userForm.enable())
      )
      .subscribe((consentAccepted: boolean) => {
        if (!consentAccepted) {
          return;
        }

        const dialogRef = this.dialog.open(CaptchaDialogComponent, {
          disableClose: true,
          maxWidth: "100vw",
          maxHeight: "100vh",
        });

        dialogRef
          .afterClosed()
          .pipe(takeUntil(this.onDestroy$))
          .subscribe((res) => {
            if (res) {
              const user: IRegisterDetails = {
                email: this.userForm.controls.email.value,
                password: this.userForm.controls.password.value,
                name: this.userForm.controls.name.value,
                firstname: this.userForm.controls.firstname.value,
                birthdate: this.formatDate(this.userForm.controls.birthdate.value),
                langCode: this.sessionService.userLang,
                sex: this.userForm.controls.sex.value,
                phone: (this.userForm.controls.phone.value as unknown as INgxIntlTelInput).nationalNumber.replace(/\s/g, ""),
                phoneIndicator: (this.userForm.controls.phone.value as unknown as INgxIntlTelInput).dialCode,
                street: this.userForm.controls.street.value,
                number: this.userForm.controls.number.value,
                city: this.userForm.controls.city.value,
                postalCode: this.userForm.controls.postalCode.value,
                country: this.userForm.controls.country.value,
                nationalNumber: this.userForm.controls.nationalNumber.value,
              };
              this.registrationService
                .createUser(user)
                .pipe(
                  first(),
                  finalize(() => this.sessionService.clearAccountCreationToken())
                )
                .subscribe({
                  next: (response) => {
                    if (response) {
                      this.dialog.open(EmailConfirmationComponent, {
                        disableClose: true,
                        data: { email: user.email },
                      });
                    }
                  },
                  error: (err) => {
                    if (err.status === 404) {
                      this.sessionService.clearGuestUUID();
                    }
                    this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", {
                      duration: 5000,
                    });
                  },
                });
            }
          });
      });
  }
}
